import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import renderHTML from "react-render-html";
import * as notificationActions from "../../actions/notificationActions";
import * as organisationActions from "../../actions/organisationActions";
import * as stripeActions from "../../actions/stripeActions";
import * as signupActions from "../../actions/signupActions";
import PageContainer from "../Global/Containers/PageContainer";
import ModalContainer from "../Global/Modals/ModalContainer";
import ConfirmModal from "../Global/Modals/ConfirmModal";
import Icon from "../Global/Icons/Icon";
import SignupForm from "./SignupForm";

class SignupPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activePage: 1,
      activePlanType: `premium`,
      activeBillingFrequency: `year`,
      acceptedTerms: false,
      signupOptionsLoading: true,
      applyDiscountLoading: false,
      referralModalOpen: false,
      submitting: false,
      takingTheirTime: false,
    };
  }

  componentWillMount() {
    this.setHeroText();
    this.startPageTimer();
  }

  componentDidMount() {
    this.props.actions.getAgeCategories();
  }

  componentWillReceiveProps(nextProps) {
    // If there is a query param in the URL to set a predefined plan type
    if (nextProps.location.query.plan_type) {
      this.setDefaultPlanType(nextProps.location.query.plan_type);
    }

    // If there is a query param in the URL to set a predefined billing frequency
    if (nextProps.location.query.billing_frequency) {
      this.setDefaultBillingFrequency(
        nextProps.location.query.billing_frequency,
      );
    }

    // If there is a query param in the URL to flag that they have been redirected
    if (nextProps.location.query.referral) {
      this.showRedirectModal(nextProps.location.query.referral);
    }

    if (this.props.signup !== nextProps.signup) {
      this.setState({ signupOptionsLoading: false });

      // If this specific signup doesn't allow users to pay monthly, then we need to change the active billing frequency
      if (!nextProps.signup.supported_frequency.includes(`month`)) {
        this.setState({ activeBillingFrequency: `year` });
      }

      // If this specific signup doesn't allow users to sign up to the premium plan, then we need to change the active plan type
      if (!nextProps.signup.plans.includes(`premium`)) {
        this.setState({ activePlanType: nextProps.signup.plans[0] });
      }
    }

    // If the user has entered a discount code and we've had a response back from Stripe API
    if (this.props.stripe.discount !== nextProps.stripe.discount) {
      this.handleDiscountResponse(nextProps.stripe.discount);
    }
  }

  setDefaultPlanType = (planType) => {
    this.setState({ activePlanType: planType });
  };

  setDefaultBillingFrequency = (billingFrequency) => {
    this.setState({ activeBillingFrequency: billingFrequency });
  };

  setHeroText = () => {
    let heroText;
    switch (this.state.activePage) {
      case 1:
        heroText = `A plan to suit <br /> everyone`;
        break;
      case 2:
        heroText = `Account details, security <br /> and billing`;
        break;
      case 3:
        heroText = `Summary and <br /> create account`;
      // no default
    }
    this.setState({ heroText });
  };

  startPageTimer = () => {
    let seconds = 0;
    let timer;

    const incrementSeconds = () => {
      seconds += 1;
      if (seconds >= 10) {
        this.setState({ takingTheirTime: true });
        clearInterval(timer);
      }
    };

    timer = setInterval(incrementSeconds, 1000);
  };

  showRedirectModal = (referral) => {
    switch (referral) {
      case `ayso`:
        this.setState({ referralModalOpen: true });
      // no default
    }
  };

  handleDiscountResponse = (discount) => {
    this.setState({ applyDiscountLoading: false });
    if (discount.exists === true) {
      this.props.actions.addNotification({
        message: `Promotion code ${discount.code} successfully applied (${discount.percent_off}% off)`,
        level: `success`,
        autoDismiss: 0,
      });
    } else {
      this.props.actions.addNotification({
        message: `The promotional code you have entered has not been recognised. Please confirm the code and try again.`,
        level: `error`,
        autoDismiss: 0,
      });
    }
  };

  changePage = (pageNumber) => {
    this.setState({ activePage: pageNumber }, () => {
      this.setHeroText();
    });
  };

  handleBackClick = () => {
    this.changePage(this.state.activePage - 1);
  };

  handleContinueClick = () => {
    window.scroll(0, 0);
    this.changePage(this.state.activePage + 1);
  };

  handleChangePageClick = (pageNumber) => {
    this.changePage(pageNumber);
  };

  handleAcceptTermsChange = () => {
    this.setState({ acceptedTerms: !this.state.acceptedTerms });
  };

  handleApplyDiscountClick = (discountCode) => {
    this.setState({ applyDiscountLoading: true });
    this.props.actions.checkDiscountCodeIsValid(discountCode);
  };

  closeModal = (modal) => {
    this.setState({ [modal]: false });
  };

  handleCreateAccountSubmit = (values) => {
    this.setState({ submitting: true });
    // If the user hasn't accepted the terms and conditions
    if (this.state.acceptedTerms === false) {
      this.stopSubmission();
      this.props.actions.addNotification({
        message: `You must agree to our terms in order to create an account`,
        level: `warning`,
        autoDismiss: 0,
      });
      // Basic plan doesn't require Stripe token or card details
    } else {
      // Empty string has to be passed as token
      this.triggerReCaptchaValidation(values, ``);
    }
  };

  triggerReCaptchaValidation = (values, stripeToken) => {
    // Google invisible reCAPTCHA markup https://developers.google.com/recaptcha/docs/invisible#example
    let sitekey = ``;
    if (
      window.location.hostname === `localhost` ||
      window.location.hostname === `127.0.0.1`
    ) {
      sitekey = `6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI`;
    } else {
      sitekey = `6LfhPIoUAAAAABee-MD-AggJtGfYXvcebtJNwoAK`;
    }

    grecaptcha.render(`reCaptcha`, {
      sitekey,
      size: `invisible`,
      callback: (reCaptchaToken) => {
        this.signupUser(values, stripeToken, reCaptchaToken);
      },
    });
    grecaptcha.execute();
  };

  signupUser = (values, stripeToken, reCaptchaToken) => {
    const data = {};
    const {
      firstName,
      lastName,
      emailAddress,
      isCoach,
      isPlayer,
      coachAgeGroup,
      password,
      planType,
      billingFrequency,
      discountCode,
      areaCode1,
      areaNo1,
      telNo1,
      newsletters,
    } = values;
    const planString = `${planType}_${this.props.signup.currency}_${billingFrequency}`;

    data.captchaToken = reCaptchaToken;
    data.plan = planString;
    data.firstName = firstName;
    data.lastName = lastName;
    data.emailAddress = emailAddress;
    data.isCoach = isCoach;
    data.isPlayer = isPlayer;
    data.ages = coachAgeGroup;
    data.password = password;
    data.areaCode1 = areaCode1;
    data.areaNo1 = areaNo1;
    data.telNo1 = telNo1;
    data.discount_code = discountCode;
    data.newsLetters = newsletters;
    data.successUrl = `/thank-you?accountType=${planString}`;
    data.cancelUrl = `/thank-you?accountType=${planString}`;
    // No promotion
    data.promotion = false;

    this.props.actions
      .signup(data)
      .then(() => this.stopSubmission())
      .catch((res) => {
        this.stopSubmission();
        grecaptcha.reset();
        // If email is already in use
        if (res.statusCode === 409) {
          this.changePage(2);
        }
      });
  };

  stopSubmission = () => {
    this.setState({ submitting: false });
  };

  render() {
    const { signup, initialData, stripe, organisation } = this.props;
    const {
      activePage,
      heroText,
      activePlanType,
      activeBillingFrequency,
      signupOptionsLoading,
      acceptedTerms,
      applyDiscountLoading,
      referralModalOpen,
      submitting,
      takingTheirTime,
    } = this.state;

    const currentPage = this.props.location.pathname;

    return (
      <PageContainer classes="signup-page bg-white">
        <h1 className="visuallyhidden">Signup</h1>

        <SignupForm
          signup={signup}
          data={initialData}
          stripe={stripe}
          activePage={activePage}
          heroText={heroText}
          activePlanType={activePlanType}
          activeBillingFrequency={activeBillingFrequency}
          signupOptionsLoading={signupOptionsLoading}
          onBackClick={this.handleBackClick}
          onContinueClick={this.handleContinueClick}
          acceptedTerms={acceptedTerms}
          onAcceptTermsChange={this.handleAcceptTermsChange}
          onChangePageClick={this.handleChangePageClick}
          onApplyDiscountClick={this.handleApplyDiscountClick}
          applyDiscountLoading={applyDiscountLoading}
          onSubmit={this.handleCreateAccountSubmit}
          submitting={submitting}
          takingTheirTime={takingTheirTime}
          currentPage={currentPage}
          ageCategories={organisation.availableAgeCategories}
        />

        <ModalContainer
          isOpen={referralModalOpen}
          closeModal={() => this.closeModal(`referralModalOpen`)}
          label="Referral modal"
          size="l"
          classes="modal-inner--upsell text-center"
        >
          <ConfirmModal
            title="You have been redirected"
            confirmText="I understand"
            onConfirmClick={() => this.closeModal(`referralModalOpen`)}
            onCloseClick={() => this.closeModal(`referralModalOpen`)}
          >
            <Icon name="ayso-logo" size="xxl" classes="m-b-5" />
            <p className="like-h5 fw-medium">
              You attempted to signup to The Coaching Manual via our AYSO
              platform. Unfortunately, this platform is only available to those
              in the US.
            </p>
            <p>
              You can still enjoy the features and benefits of The Coaching
              Manual like thousands of other coaches and players by signing up
              on this page.
            </p>
            <p>
              {renderHTML(
                `If you think this is an error please email us at <a href="mailto:help@thecoachingmanual.com">help@thecoachingmanual.com</a>.`,
              )}
            </p>
          </ConfirmModal>
        </ModalContainer>
      </PageContainer>
    );
  }
}

SignupPage.propTypes = {
  signup: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  stripe: PropTypes.object.isRequired,
  organisation: PropTypes.object.isRequired,
  initialData: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
};

function mapStateToProps(state) {
  const prefillAreaNo = () => {
    if (state.signup.currency === `GBP`) {
      return `+44`;
    }
    return `+1`;
  };

  const prefillAreaCode = () => {
    if (state.signup.currency === `GBP`) {
      return `GB`;
    }
    return `US`;
  };

  const data = {
    plan: ``,
    firstName: ``,
    lastName: ``,
    emailAddress: ``,
    isCoach: true,
    isPlayer: false,
    coachAgeGroup: [],
    playerAgeGroup: [],
    areaNo1: prefillAreaNo() || ``,
    areaCode1: prefillAreaCode() || ``,
    telNo1: ``,
    password: ``,
    newsletters: false,
    token: ``,
    discountCode: ``,
  };

  return {
    signup: state.signup,
    stripe: state.stripe,
    organisation: state.organisation,
    initialData: data,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      Object.assign({}, notificationActions, signupActions, stripeActions, organisationActions),
      dispatch,
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(SignupPage);



// WEBPACK FOOTER //
// ./library/app/components/Signup/SignupPage.js